// Shift Master — configure the working shifts used by HR / Production
// / Quality across the whole ERP.
//
// Every shift is plant-scoped. The plant picker in the top bar drives
// which plant's shifts you see. To set up shifts for a new mill on
// day-1, create at least: Shift A / B / C (rotating 8-hour) and a
// General shift for office staff.

import { useState } from 'react'
import {
  App, Button, Form, Input, InputNumber, Modal, Space, Switch, Table, Tag,
  TimePicker, Tooltip, Typography,
} from 'antd'
import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { shiftsApi } from '@/features/masters/shifts/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { Shift } from '@/shared/types/master'

dayjs.extend(customParseFormat)
const { Text } = Typography

const TIME_FMT = 'HH:mm'
const API_TIME_FMT = 'HH:mm:ss'

// Quick-fill presets — Indian textile mill conventions. Tapping a preset
// fills the create form with sensible defaults; supervisor can still edit
// any field before saving.
const PRESETS: Array<{
  code: string; name: string; start: string; end: string; night: boolean
}> = [
  { code: 'A',   name: 'Shift A (Morning)',     start: '06:00', end: '14:00', night: false },
  { code: 'B',   name: 'Shift B (Afternoon)',   start: '14:00', end: '22:00', night: false },
  { code: 'C',   name: 'Shift C (Night)',       start: '22:00', end: '06:00', night: true  },
  { code: 'GEN', name: 'General (Day shift)',   start: '09:00', end: '17:30', night: false },
]

interface FormState {
  shift_code: string
  shift_name: string
  start_time: Dayjs | null
  end_time: Dayjs | null
  is_night_shift: boolean
  break_minutes: number
  is_active: boolean
}

const BLANK: FormState = {
  shift_code: '',
  shift_name: '',
  start_time: null,
  end_time: null,
  is_night_shift: false,
  // Mill default: 40 lunch + 10 tea + 10 toilet = 60 min for every shift.
  // Variance task generator reads this to compute effective work hours.
  break_minutes: 60,
  is_active: true,
}

export default function ShiftsPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const canCreate = usePermission('shift.create')
  const canUpdate = usePermission('shift.update')

  const [editing, setEditing] = useState<Shift | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState<FormState>(BLANK)

  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ['shifts-master', plantId],
    queryFn: () => shiftsApi.list({ plant_id: plantId }),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: () => {
      if (!plantId) throw new Error('Plant context missing')
      if (!form.shift_code.trim()) throw new Error('Code is required')
      if (!form.shift_name.trim()) throw new Error('Name is required')
      if (!form.start_time || !form.end_time) throw new Error('Start and end time required')
      return shiftsApi.create({
        plant_id: plantId,
        shift_code: form.shift_code.trim().toUpperCase(),
        shift_name: form.shift_name.trim(),
        start_time: form.start_time.format(API_TIME_FMT),
        end_time: form.end_time.format(API_TIME_FMT),
        is_night_shift: form.is_night_shift,
        break_minutes: form.break_minutes,
        is_active: form.is_active,
      })
    },
    onSuccess: () => {
      message.success('Shift created.')
      qc.invalidateQueries({ queryKey: ['shifts-master'] })
      setCreateOpen(false); setForm(BLANK)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(
      e?.response?.data?.detail ?? e?.message ?? 'Create failed'),
  })

  const updateMut = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error('No shift selected')
      if (!form.start_time || !form.end_time) throw new Error('Start and end time required')
      return shiftsApi.update(editing.id, {
        shift_name: form.shift_name.trim(),
        start_time: form.start_time.format(API_TIME_FMT),
        end_time: form.end_time.format(API_TIME_FMT),
        is_night_shift: form.is_night_shift,
        break_minutes: form.break_minutes,
        is_active: form.is_active,
      })
    },
    onSuccess: () => {
      message.success('Shift updated.')
      qc.invalidateQueries({ queryKey: ['shifts-master'] })
      setEditing(null); setForm(BLANK)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(
      e?.response?.data?.detail ?? e?.message ?? 'Update failed'),
  })

  const openCreate = (preset?: typeof PRESETS[number]) => {
    if (preset) {
      setForm({
        shift_code: preset.code,
        shift_name: preset.name,
        start_time: dayjs(preset.start, TIME_FMT),
        end_time: dayjs(preset.end, TIME_FMT),
        is_night_shift: preset.night,
        break_minutes: 60,
        is_active: true,
      })
    } else {
      setForm(BLANK)
    }
    setCreateOpen(true)
  }

  const openEdit = (s: Shift) => {
    setEditing(s)
    setForm({
      shift_code: s.shift_code,
      shift_name: s.shift_name,
      start_time: dayjs(s.start_time, API_TIME_FMT),
      end_time: dayjs(s.end_time, API_TIME_FMT),
      is_night_shift: s.is_night_shift,
      break_minutes: s.break_minutes ?? 60,
      is_active: s.is_active,
    })
  }

  const closeEditCreate = () => {
    setCreateOpen(false); setEditing(null); setForm(BLANK)
  }

  return (
    <div>
      <PageHeader
        title="Shift Master"
        subtitle="Configure the working shifts used everywhere in the ERP — attendance, allocation, production, quality."
        extra={
          canCreate && (
            <Space>
              <Button type="primary" icon={<PlusOutlined />}
                onClick={() => openCreate()}>
                New shift
              </Button>
            </Space>
          )
        }
      />

      {/* Quick-fill presets — visible only when there are zero shifts so
          a new mill can be set up in 4 taps. */}
      {!isLoading && shifts.length === 0 && canCreate && (
        <div style={{
          padding: 16, background: '#f6ffed',
          border: '1px solid #b7eb8f', borderRadius: 8, marginBottom: 16,
        }}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            No shifts yet — quick setup
          </Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Tap a preset to pre-fill the form, edit anything you need, then save.
          </Text>
          <Space wrap>
            {PRESETS.map((p) => (
              <Button key={p.code} onClick={() => openCreate(p)}>
                {p.name} — {p.start}–{p.end}
              </Button>
            ))}
          </Space>
        </div>
      )}

      <Table<Shift>
        rowKey="id" size="small" loading={isLoading} dataSource={shifts}
        pagination={false}
        columns={[
          { title: 'Code', dataIndex: 'shift_code', width: 100,
            render: (v: string) => <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text> },
          { title: 'Name', dataIndex: 'shift_name' },
          { title: 'Start', dataIndex: 'start_time', width: 100,
            render: (v: string) => dayjs(v, API_TIME_FMT).format(TIME_FMT) },
          { title: 'End', dataIndex: 'end_time', width: 100,
            render: (v: string) => dayjs(v, API_TIME_FMT).format(TIME_FMT) },
          { title: 'Night', dataIndex: 'is_night_shift', width: 80,
            render: (v: boolean) => v ? <Tag color="purple">Night</Tag> : null },
          {
            title: (
              <Tooltip title="Total scheduled break per shift (lunch + tea + toilet). Subtracted from shift duration to compute effective work hours used by the Variance Task system.">
                <span>Breaks</span>
              </Tooltip>
            ),
            dataIndex: 'break_minutes', width: 100,
            render: (v: number) => {
              if (v == null) return <Text type="secondary">—</Text>
              if (v === 0) return (
                <Tooltip title="Zero breaks is unusual. Variance math will treat full shift duration as working hours.">
                  <Tag color="red">0 min ⚠</Tag>
                </Tooltip>
              )
              return <Text>{v} min</Text>
            },
          },
          { title: 'Active', dataIndex: 'is_active', width: 90,
            render: (v: boolean) => v
              ? <Tag color="green">Active</Tag>
              : <Tag>Inactive</Tag> },
          {
            title: '', key: 'actions', width: 80,
            render: (_, r) => canUpdate ? (
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
            ) : null,
          },
        ]}
      />

      <Modal
        open={createOpen || !!editing}
        onCancel={closeEditCreate}
        title={editing ? `Edit ${editing.shift_code}` : 'New shift'}
        onOk={() => editing ? updateMut.mutate() : createMut.mutate()}
        confirmLoading={createMut.isPending || updateMut.isPending}
        okText={editing ? 'Save changes' : 'Create'}
        width={520}
      >
        <Form layout="vertical">
          <Space style={{ width: '100%' }}>
            <Form.Item label="Code" required style={{ flex: 1 }}>
              <Input
                value={form.shift_code}
                disabled={!!editing}     // code immutable after creation
                placeholder="A / B / C / GEN"
                onChange={(e) => setForm((f) => ({ ...f, shift_code: e.target.value }))}
                style={{ width: 150 }}
              />
            </Form.Item>
            <Form.Item label="Name" required style={{ flex: 1 }}>
              <Input
                value={form.shift_name}
                placeholder="Shift A (Morning)"
                onChange={(e) => setForm((f) => ({ ...f, shift_name: e.target.value }))}
                style={{ minWidth: 280 }}
              />
            </Form.Item>
          </Space>

          <Space style={{ width: '100%' }} size={24}>
            <Form.Item label="Start time" required>
              <TimePicker
                format={TIME_FMT}
                value={form.start_time}
                minuteStep={5}
                onChange={(t) => setForm((f) => ({ ...f, start_time: t }))}
              />
            </Form.Item>
            <Form.Item label="End time" required>
              <TimePicker
                format={TIME_FMT}
                value={form.end_time}
                minuteStep={5}
                onChange={(t) => setForm((f) => ({ ...f, end_time: t }))}
              />
            </Form.Item>
            <Form.Item
              label={
                <Tooltip title="40 lunch + 10 tea + 10 toilet = 60 min default. Subtracted from shift duration when computing effective work hours.">
                  <span>Breaks (min)</span>
                </Tooltip>
              }
              required
            >
              <InputNumber
                value={form.break_minutes}
                min={0}
                max={600}
                step={5}
                onChange={(v) => setForm((f) => ({ ...f, break_minutes: Number(v ?? 0) }))}
                style={{ width: 110 }}
              />
            </Form.Item>
          </Space>

          <Form.Item>
            <Space size={24}>
              <Space>
                <Switch
                  checked={form.is_night_shift}
                  onChange={(v) => setForm((f) => ({ ...f, is_night_shift: v }))}
                />
                <Text>Night shift</Text>
              </Space>
              <Space>
                <Switch
                  checked={form.is_active}
                  onChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
                />
                <Text>Active</Text>
              </Space>
            </Space>
          </Form.Item>

          <div style={{ background: '#f5f5f5', padding: 10, borderRadius: 6,
                        fontSize: 12, color: '#595959' }}>
            <Text strong>Night-shift rule:</Text> turn this ON when end_time
            is earlier than start_time (e.g. 22:00–06:00). The attendance
            engine uses it to compute hours correctly across midnight.
          </div>
        </Form>
      </Modal>
    </div>
  )
}
